import type {
  AuditEntry,
  Candidate,
  CandidateCreate,
  CandidateSearch,
  CandidateStatus,
  CandidateUpdate,
  Document as DocumentRecord,
  InterviewCreate,
  InterviewEntry,
  InterviewUpdate,
  Search,
  SearchCreate,
  SearchUpdate,
  StageDef,
  UserCreate,
  UserDto,
  UserUpdate,
} from "@ow/shared";
import { api } from "./api";

/** Edge enriched with its search's display fields (matches the server's getWithRelations). */
export interface CandidateEdge {
  id: string;
  searchId: string;
  searchTitle: string;
  searchTier: string;
  /** stage key within the edge's role ladder */
  stage: string;
  status: CandidateStatus;
  stageEnteredAt: string;
}
export interface CandidateDetail {
  candidate: Candidate;
  edges: CandidateEdge[];
  documents: DocumentRecord[];
  interviews: (InterviewEntry & { searchTitle: string })[];
}

export interface ImportResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  rows: { line: number; name: string; status: string; reason?: string }[];
}

const base = "/v1/admin";

export const adminApi = {
  searches: {
    list: () => api.get<Search[]>(`${base}/searches`),
    get: (id: string) => api.get<Search>(`${base}/searches/${id}`),
    create: (body: SearchCreate) => api.post<Search>(`${base}/searches`, body),
    update: (id: string, body: SearchUpdate) => api.patch<Search>(`${base}/searches/${id}`, body),
    archive: (id: string) => api.del<Search>(`${base}/searches/${id}`),
    /** Replace the pipeline ladder — pre-launch only (server 409s once live). */
    setPipeline: (id: string, stages: StageDef[]) => api.put<Search>(`${base}/searches/${id}/pipeline`, { stages }),
    /** Take a draft role live; locks the pipeline. Irreversible. */
    launch: (id: string) => api.post<Search>(`${base}/searches/${id}/launch`, {}),
  },
  candidates: {
    list: () => api.get<Candidate[]>(`${base}/candidates`),
    get: (id: string) => api.get<CandidateDetail>(`${base}/candidates/${id}`),
    create: (body: CandidateCreate) => api.post<Candidate>(`${base}/candidates`, body),
    update: (id: string, body: CandidateUpdate) => api.patch<Candidate>(`${base}/candidates/${id}`, body),
    remove: (id: string) => api.del<void>(`${base}/candidates/${id}`),
    quickAdd: (body: { fullName: string; searchId: string; stage: string }) =>
      api.post<{ candidate: Candidate; edge: CandidateSearch }>(`${base}/candidates/quick-add`, body),
  },
  links: {
    create: (candidateId: string, body: { searchId: string; stage?: string; status?: CandidateStatus }) =>
      api.post<CandidateSearch>(`${base}/candidates/${candidateId}/searches`, body),
    moveStage: (linkId: string, body: { stage?: string; status?: CandidateStatus }) =>
      api.patch<CandidateSearch>(`${base}/candidate-searches/${linkId}/stage`, body),
    remove: (linkId: string) => api.del<void>(`${base}/candidate-searches/${linkId}`),
  },
  interviews: {
    listFor: (linkId: string) => api.get<InterviewEntry[]>(`${base}/candidate-searches/${linkId}/interviews`),
    create: (linkId: string, body: InterviewCreate) =>
      api.post<InterviewEntry>(`${base}/candidate-searches/${linkId}/interviews`, body),
    update: (id: string, body: InterviewUpdate) => api.patch<InterviewEntry>(`${base}/interviews/${id}`, body),
    remove: (id: string) => api.del<void>(`${base}/interviews/${id}`),
  },
  users: {
    list: () => api.get<UserDto[]>(`${base}/users`),
    create: (body: UserCreate) => api.post<UserDto>(`${base}/users`, body),
    update: (id: string, body: UserUpdate) => api.patch<UserDto>(`${base}/users/${id}`, body),
    remove: (id: string) => api.del<void>(`${base}/users/${id}`),
  },
  audit: {
    list: (entity?: string, id?: string) => {
      const q = new URLSearchParams();
      if (entity) q.set("entity", entity);
      if (id) q.set("id", id);
      return api.get<AuditEntry[]>(`${base}/audit${q.toString() ? `?${q}` : ""}`);
    },
  },
  importCsv: async (file: File): Promise<ImportResult> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api${base}/candidates/import`, { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) throw new Error(`import failed: ${res.status}`);
    return (await res.json()) as ImportResult;
  },
};

export const files = {
  downloadUrl: (id: string) => `/api/v1/files/${id}`,
  async upload(candidateId: string, file: File, docType = "cv"): Promise<DocumentRecord> {
    const fd = new FormData();
    fd.append("candidateId", candidateId);
    fd.append("docType", docType);
    fd.append("file", file);
    const res = await fetch("/api/v1/files", { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    return (await res.json()) as DocumentRecord;
  },
  remove: (id: string) => api.del<void>(`/v1/files/${id}`),
};
